// modules/freshcards/src/ThemeAPI.ts
//
// 2026-09-03 (Phase 12) — ThemeAPI.
//
// Typed surface for AI agents and external apps to apply white-label
// themes to FreshCards. Supports three levels of theming:
//   1. Account-level: applies to the whole workspace
//   2. Page-level: applies to a single page (overrides account)
//   3. Block-level: applies to a single block (overrides page)
//
// This module is PURE: no React. The companion ThemeEngine (in
// ThemeEngine.ts) renders the active theme as CSS variables on the
// document root, which the existing components already consume via
// `var(--accent)`, `var(--bg)`, etc.
//
// Persistence: account theme lives in localStorage under
// `freshcards:theme`. Page-level overrides live on the page's
// `themeOverride` field. The Theme API is a thin wrapper over the
// page's existing update method.

import type { FreshCardsPage, PageBlock, FreshCardsContextValue } from './types';

// ---------------------------------------------------------------------------
// Theme shape
// ---------------------------------------------------------------------------

export interface Palette {
  /** Primary accent color (used for highlights, buttons). */
  accent: string;
  /** Background color (page surface). */
  bg: string;
  /** Subtle background (cards, modals). */
  sub: string;
  /** Foreground text. */
  text: string;
  /** Subtle text (labels, dim text). */
  textMuted: string;
  /** Border color (subtle dividers). */
  border: string;
  /** Highlight background (e.g. selected row). */
  accentBg: string;
  /** Icon tone (None, blue, red, yellow, etc.). */
  iconTone?: 'none' | 'blue' | 'red' | 'yellow' | 'green' | 'purple' | 'orange' | 'pink';
  /** Optional accent background used for AI surfaces. */
  aiAccent?: string;
}

export interface Typography {
  /** Body font family. */
  fontFamily: string;
  /** Heading font family. Falls back to fontFamily. */
  headingFontFamily?: string;
  /** Base font size in px. */
  baseFontSize: number;
  /** Heading scale — multiplier on base for h1..h3. */
  headingScale: [number, number, number];
}

export interface CardGeometry {
  /** Card border radius in px. */
  radius: number;
  /** Card shadow strength: 'none' | 'sm' | 'md' | 'lg'. */
  shadow: 'none' | 'sm' | 'md' | 'lg';
  /** Card border width in px. */
  borderWidth: number;
  /** Card padding in px. */
  padding: number;
}

export interface CardFrontBack {
  /** Card front: shows the icon. */
  icon: string;
  /** Card front: color tone. */
  color: string;
  /** Card back: cover gradient id (one of the 16 CardCover pairs). */
  cover: string | null;
  /** Card back: shows the title. */
  titleVisible: boolean;
}

export interface SemanticBlockStyle {
  /** Cluster style: radial ring size, node color. */
  cluster: { ringSize: number; nodeColor: string; edgeColor: string };
  /** Reference style: chip background + text colors. */
  reference: { chipBg: string; chipText: string; chipBorder: string };
  /** Neighborhood style: same as reference (shares chip renderer). */
  neighborhood: { chipBg: string; chipText: string; chipBorder: string };
  /** Cross-db style: card background. */
  crossDb: { cardBg: string; cardBorder: string };
  /** Dashboard style: card backgrounds. */
  dashboard: { cardBg: string; cardBorder: string; hubColor: string };
}

export interface DatabaseViewStyle {
  /** Row card style — used in gallery, list, kanban. */
  rowCard: { cover: boolean; radius: number; shadow: 'none' | 'sm' | 'md' | 'lg' };
  /** Table style — column widths, row height. */
  table: { rowHeight: number; alternatingRows: boolean };
  /** Grid style — columns, gap. */
  grid: { columns: number; gap: number };
}

export interface PageTreeStyle {
  /** Icon pack name. */
  iconPack: 'default' | 'minimal' | 'playful' | 'boxed';
  /** Color pack name. */
  colorPack: 'default' | 'pastel' | 'bold' | 'monochrome';
  /** Grouping rule. */
  grouping: 'parent' | 'type' | 'favorites' | 'smart';
  /** Show section icons. */
  showSectionIcons: boolean;
}

export interface Theme {
  id: string;
  name: string;
  description?: string;
  palette: Palette;
  typography: Typography;
  geometry: CardGeometry;
  cardFrontBack: CardFrontBack;
  semanticBlocks: SemanticBlockStyle;
  databaseViews: DatabaseViewStyle;
  pageTree: PageTreeStyle;
  /** Provenance: who created this theme. */
  createdBy?: 'builtin' | 'ai' | 'user' | 'imported';
  /** When the theme was created (ISO). */
  createdAt?: string;
}

export const DEFAULT_THEME: Theme = {
  id: 't-default',
  name: 'Default',
  description: 'The default FreshCards theme (dark surfaces, blue accent).',
  palette: {
    accent: '#5b86e5',
    bg: '#0a0a0a',
    sub: '#1a1a1a',
    text: '#f5f5f5',
    textMuted: '#aaa',
    border: '#2a2a2a',
    accentBg: '#2a3a5a',
    iconTone: 'none',
    aiAccent: '#7c5fff',
  },
  typography: {
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    baseFontSize: 14,
    headingScale: [1.6, 1.3, 1.15],
  },
  geometry: { radius: 8, shadow: 'sm', borderWidth: 1, padding: 12 },
  cardFrontBack: {
    icon: '📄',
    color: 'gray',
    cover: null,
    titleVisible: true,
  },
  semanticBlocks: {
    cluster: { ringSize: 16, nodeColor: '#5b86e5', edgeColor: '#5b86e5' },
    reference: { chipBg: '#2a3a5a', chipText: '#f5f5f5', chipBorder: '#5b86e5' },
    neighborhood: { chipBg: '#2a3a5a', chipText: '#f5f5f5', chipBorder: '#5b86e5' },
    crossDb: { cardBg: '#1a1a1a', cardBorder: '#2a2a2a' },
    dashboard: { cardBg: '#1a1a1a', cardBorder: '#2a2a2a', hubColor: '#5b86e5' },
  },
  databaseViews: {
    rowCard: { cover: true, radius: 6, shadow: 'sm' },
    table: { rowHeight: 32, alternatingRows: true },
    grid: { columns: 3, gap: 12 },
  },
  pageTree: {
    iconPack: 'default',
    colorPack: 'default',
    grouping: 'smart',
    showSectionIcons: true,
  },
  createdBy: 'builtin',
  createdAt: '2026-01-01T00:00:00Z',
};

// ---------------------------------------------------------------------------
// Theme storage
// ---------------------------------------------------------------------------

const ACCOUNT_THEME_KEY = 'freshcards:theme';
const PAGE_THEME_FIELD = 'themeOverride';

export function loadAccountTheme(): Theme {
  if (typeof localStorage === 'undefined') return DEFAULT_THEME;
  try {
    const raw = localStorage.getItem(ACCOUNT_THEME_KEY);
    if (!raw) return DEFAULT_THEME;
    const parsed = JSON.parse(raw) as Theme;
    if (!parsed.id || !parsed.palette) return DEFAULT_THEME;
    return { ...DEFAULT_THEME, ...parsed };
  } catch {
    return DEFAULT_THEME;
  }
}

export function saveAccountTheme(theme: Theme): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(ACCOUNT_THEME_KEY, JSON.stringify(theme));
}

export function clearAccountTheme(): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.removeItem(ACCOUNT_THEME_KEY);
}

// ---------------------------------------------------------------------------
// Page-level theme override
// ---------------------------------------------------------------------------

export function getPageTheme(
  ctx: FreshCardsContextValue,
  pageId: string,
): Theme {
  const account = loadAccountTheme();
  const page = ctx.getPageById(pageId);
  if (!page) return account;
  // Page-level override is stored in the (typed) `themeOverride` field.
  // Page extends with themeOverride, but at the type level we look it up
  // as an open record to avoid breaking the existing schema.
  const override = (page as unknown as { themeOverride?: Theme }).themeOverride;
  if (!override) return account;
  return { ...account, ...override, palette: { ...account.palette, ...override.palette } };
}

// ---------------------------------------------------------------------------
// API
// ---------------------------------------------------------------------------

export interface ThemeAPI {
  get(): Theme;
  set(theme: Theme): void;
  reset(): void;
  export(): string;
  import(json: string): Theme | null;
  getForPage(ctx: FreshCardsContextValue, pageId: string): Theme;
  setForPage(ctx: FreshCardsContextValue, pageId: string, theme: Partial<Theme>): void;
  clearForPage(ctx: FreshCardsContextValue, pageId: string): void;
  /** Returns the active theme for a page (account + page override). */
  resolve(ctx: FreshCardsContextValue, pageId?: string): Theme;
}

export function makeThemeAPI(): ThemeAPI {
  return {
    get() { return loadAccountTheme(); },
    set(theme) { saveAccountTheme(theme); },
    reset() { clearAccountTheme(); },
    export() { return JSON.stringify(loadAccountTheme(), null, 2); },
    import(json) {
      try {
        const parsed = JSON.parse(json) as Theme;
        if (!parsed.id || !parsed.palette) return null;
        const theme: Theme = { ...DEFAULT_THEME, ...parsed, createdBy: 'imported' };
        saveAccountTheme(theme);
        return theme;
      } catch {
        return null;
      }
    },
    getForPage(ctx, pageId) { return getPageTheme(ctx, pageId); },
    setForPage(ctx, pageId, theme) {
      const page = ctx.getPageById(pageId);
      if (!page) return;
      const account = loadAccountTheme();
      const merged: Theme = {
        ...account,
        ...theme,
        id: account.id,
        palette: { ...account.palette, ...(theme.palette ?? {}) },
        typography: { ...account.typography, ...(theme.typography ?? {}) },
        geometry: { ...account.geometry, ...(theme.geometry ?? {}) },
        cardFrontBack: { ...account.cardFrontBack, ...(theme.cardFrontBack ?? {}) },
        semanticBlocks: { ...account.semanticBlocks, ...(theme.semanticBlocks ?? {}) },
        databaseViews: { ...account.databaseViews, ...(theme.databaseViews ?? {}) },
        pageTree: { ...account.pageTree, ...(theme.pageTree ?? {}) },
        createdBy: 'user',
        createdAt: new Date().toISOString(),
      };
      ctx.updatePage(pageId, { themeOverride: merged } as any);
    },
    clearForPage(ctx, pageId) {
      const page = ctx.getPageById(pageId);
      if (!page) return;
      // Remove the override by writing the current account theme (a
      // no-op override that matches the account).
      ctx.updatePage(pageId, { themeOverride: undefined } as any);
    },
    resolve(ctx, pageId) {
      if (pageId) return getPageTheme(ctx, pageId);
      return loadAccountTheme();
    },
  };
}

// ---------------------------------------------------------------------------
// Block-level style — applies a SemanticBlockStyle to a single block
// ---------------------------------------------------------------------------

export interface BlockStyleOverride {
  blockId: string;
  /** Which style slot to override. */
  slot: keyof SemanticBlockStyle;
  /** Inline patch — partial of the slot's shape. */
  patch: Partial<SemanticBlockStyle[keyof SemanticBlockStyle]>;
}

export function mergeBlockStyle(
  base: SemanticBlockStyle,
  override: BlockStyleOverride | null,
): SemanticBlockStyle {
  if (!override) return base;
  const slotValue = base[override.slot] as Record<string, any>;
  return {
    ...base,
    [override.slot]: { ...slotValue, ...override.patch },
  } as SemanticBlockStyle;
}
